/**
 * 画面の組み立て。入口（ログイン・招待）と本体（アウトライン）を切り替える。
 *
 * 意味論は `@biside/domain`、接続は `sync.ts`、編集は `outline.ts` にある。ここは配線だけ。
 */

import { boardHTML, detailHTML, type BoardCard, type BoardColumn, type CardDetail } from "./board.ts";
import { OutlineStore } from "./store.ts";
import { OutlineView } from "./outline.ts";
import { SyncLink, type LinkState } from "./sync.ts";
import type { SyncOp } from "@biside/domain";

const $ = <T extends HTMLElement>(sel: string) => document.querySelector<T>(sel)!;

async function api(path: string, init: RequestInit = {}) {
  const res = await fetch(path, { credentials: "same-origin", ...init });
  const text = await res.text();
  return { ok: res.ok, status: res.status, json: text ? JSON.parse(text) : null };
}
const post = (path: string, body: unknown) =>
  api(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

/**
 * 招待トークン。**入口は `/join/<token>`** で、`?invite=` は前に配ったリンクのための退路。
 * 生きているリンクを切らないため、両方を受ける。
 */
const inviteToken =
  location.pathname.match(/^\/join\/([A-Za-z0-9_-]+)/)?.[1] ?? new URLSearchParams(location.search).get("invite");

// ---------------------------------------------------------------------- 参加

interface Invite {
  email: string;
  workspaceId: string | null;
  workspaceTitle: string;
  invitedBy: string;
}

/**
 * 招待を受ける画面。**ここで参加まで終わる**（アプリは要らない）。
 *
 * 前の版は、招待リンクを踏むと「アカウントを作る」というログイン画面が出るだけだった。
 * 誰が何に呼んでいるのかが画面に無く、一方でメールは「アプリにリンクを貼れ」と言っていた
 * ので、入口が 2 つあるように見えていた。**押した先が終着点**であることを画面で示す。
 */
async function renderJoin(token: string, signedIn: { email: string } | null): Promise<void> {
  $("#app").innerHTML = `<div class="join" id="join"><p class="muted">読み込んでいます…</p></div>`;
  const { ok, json } = await api(`/api/invites/${token}`);
  if (!ok) {
    $("#join").innerHTML = `
      <h1 class="join-title">招待が使えません</h1>
      <p class="muted">期限が切れているか、すでに使われています。<br>招いた人にもう一度送ってもらってください。</p>`;
    return;
  }
  const inv = json as Invite;

  // **誰が・どこへ呼んでいるかを最初に出す。** 招待を受けた人向けの画面は「新規登録」とは
  // 別物として作る（招かれた人はまだ製品を知らないので、フォームより先に文脈が要る）。
  // 画面の目的は 1 つ、押すものも 1 つ。
  const head = `
    <div class="join-avatar" aria-hidden="true">${esc([...inv.workspaceTitle][0] ?? "B")}</div>
    <p class="join-from">${esc(inv.invitedBy)} さんが招待しています</p>
    <h1 class="join-title">${esc(inv.workspaceTitle)}</h1>`;

  // 既に入っている人が招待を踏んだとき。**もう一度アカウントを作らせない。**
  if (signedIn) {
    const same = signedIn.email.toLowerCase() === inv.email.toLowerCase();
    $("#join").innerHTML = `
      ${head}
      ${same
        ? `<p class="join-as">${esc(signedIn.email)} として参加します</p>
           <button class="join-primary" id="accept">参加する</button>`
        : `<p class="join-as warn">この招待は <strong>${esc(inv.email)}</strong> 宛です。<br>
             いまは ${esc(signedIn.email)} で入っています。</p>
           <button class="join-primary" id="out">ログアウトして開き直す</button>`}
      <div class="msg" id="msg"></div>
      <p class="join-fine">リンクの有効期限は 7 日です。</p>`;
    if (same) {
      $("#accept").addEventListener("click", async () => {
        const btn = $<HTMLButtonElement>("#accept");
        btn.disabled = true;
        const res = await post(`/api/invites/${token}/accept`, {});
        if (res.ok) { location.href = `/?ws=${res.json.workspaceId}`; return; }
        btn.disabled = false;
        $("#msg").textContent = res.json?.error ?? "参加できませんでした";
      });
    } else {
      $("#out").addEventListener("click", async () => {
        await post("/api/auth/sign-out", {});
        location.reload();
      });
    }
    return;
  }

  $("#join").innerHTML = `
    ${head}
    <p class="join-as">${esc(inv.email)} として参加します</p>
    <form id="joinForm">
      <label class="join-field">お名前
        <input name="name" placeholder="山田 太郎" autocomplete="name" required>
      </label>
      <label class="join-field">パスワード
        <input name="password" type="password" placeholder="10 文字以上" autocomplete="new-password"
               minlength="10" required>
      </label>
      <button class="join-primary" type="submit">参加する</button>
      <div class="msg" id="msg"></div>
    </form>
    <p class="join-fine">ここで決めた名前とパスワードが、そのままアカウントになります。<br>
      リンクの有効期限は 7 日です。</p>`;

  const form = $<HTMLFormElement>("#joinForm");
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const btn = form.querySelector<HTMLButtonElement>(".join-primary")!;
    btn.disabled = true;
    const f = Object.fromEntries(new FormData(form) as any) as Record<string, string>;
    const res = await post("/api/auth/sign-up/email", {
      email: inv.email, password: f.password, name: f.name, inviteToken: token,
    });
    if (res.ok) {
      location.href = inv.workspaceId ? `/?ws=${inv.workspaceId}` : "/";
      return;
    }
    // すでにそのメールのアカウントがある場合。**作れないのではなく、入ればいい。**
    const signIn = await post("/api/auth/sign-in/email", { email: inv.email, password: f.password });
    if (signIn.ok) {
      const accepted = await post(`/api/invites/${token}/accept`, {});
      location.href = accepted.ok ? `/?ws=${accepted.json.workspaceId}` : "/";
      return;
    }
    btn.disabled = false;
    $("#msg").textContent =
      "参加できませんでした。このメールのアカウントが既にある場合は、そのときのパスワードを入れてください。";
  });
}

const esc = (v: string) =>
  v.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]!);

/**
 * Mac アプリからの「サインインさせて」を受ける画面（`/link?challenge=…&state=…&scheme=…`）。
 *
 * **アプリにパスワードを打たせないための面。** ここで人がすることは「許可する」を 1 回押すだけで、
 * 押した結果は私用スキームでアプリへ戻る（RFC 8252 と同じ形）。合言葉そのものは渡らない。
 */
async function renderLink(signedIn: { email: string } | null): Promise<void> {
  const q = new URLSearchParams(location.search);
  const challenge = q.get("challenge") ?? "";
  const state = q.get("state") ?? "";
  const scheme = q.get("scheme") ?? "biside";
  if (!challenge) {
    $("#app").innerHTML = `<div class="join"><h1>この画面は Mac アプリから開きます</h1></div>`;
    return;
  }
  if (!signedIn) {
    // 入っていなければ、まず入ってもらう。**戻り先はこの画面**なので、入ったあとに続きへ進める。
    renderGate("signin");
    return;
  }
  $("#app").innerHTML = `
    <div class="join">
      <h1>Mac の Biside に<br>サインインしますか</h1>
      <p class="muted">${esc(signedIn.email)} として許可します。</p>
      <form id="linkForm"><button type="submit">許可する</button><div class="msg" id="msg"></div></form>
      <p class="muted small">許可すると、この Mac だけの身分証がアプリに渡ります。パスワードは渡りません。</p>
    </div>`;
  $<HTMLFormElement>("#linkForm").addEventListener("submit", async (e) => {
    e.preventDefault();
    const res = await post("/api/device/authorize", { challenge });
    if (!res.ok) {
      $("#msg").textContent = res.json?.error ?? "うまくいきませんでした";
      return;
    }
    // 戻る先はアプリ。**ここから先はブラウザの仕事ではない。**
    location.href = `${scheme}://auth?code=${encodeURIComponent(res.json.code)}&state=${encodeURIComponent(state)}`;
    $("#msg").textContent = "アプリに戻りました。この画面は閉じてかまいません。";
  });
}

// ---------------------------------------------------------------------- 入口

function renderGate(mode: "signin" | "signup", bootstrap = false): void {
  const signUp = mode === "signup";
  $("#app").innerHTML = `
    <form class="gate" id="gate">
      <h1>${signUp ? "アカウントを作る" : "Biside"}</h1>
      ${signUp && bootstrap ? `<div class="invite">まだ誰も居ません。<strong>最初の 1 人</strong>としてアカウントを作ります（以降は招待が要ります）。</div>` : ""}
      ${signUp && inviteToken ? `<div class="invite" id="inviteFor">招待を確認しています…</div>` : ""}
      ${signUp ? `<input name="name" placeholder="名前" autocomplete="name">` : ""}
      <input name="email" type="email" placeholder="メール" autocomplete="username" required>
      <input name="password" type="password" placeholder="パスワード（10 文字以上）" autocomplete="current-password" required>
      <button type="submit">${signUp ? "作る" : "ログイン"}</button>
      <div class="msg" id="msg"></div>
    </form>`;

  const form = $<HTMLFormElement>("#gate");
  if (signUp && inviteToken) {
    api(`/api/invites/${inviteToken}`).then(({ ok, json }) => {
      $("#inviteFor").textContent = ok ? `${json.email} 宛の招待です` : "招待が見つからないか、期限が切れています";
      if (ok) (form.elements.namedItem("email") as HTMLInputElement).value = json.email;
    });
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const body: Record<string, unknown> = Object.fromEntries(new FormData(form) as any);
    if (signUp) body.inviteToken = inviteToken ?? "";
    const { ok, json } = await post(signUp ? "/api/auth/sign-up/email" : "/api/auth/sign-in/email", body);
    if (ok) location.href = "/";
    else $("#msg").textContent = json?.error ?? json?.message ?? "うまくいきませんでした";
  });
}

// ---------------------------------------------------------------------- 本体

interface Workspace {
  id: string;
  title: string;
}

function renderApp(user: { email: string }, workspaces: Workspace[]): void {
  const workspaceId = new URLSearchParams(location.search).get("ws") ?? workspaces[0]!.id;

  $("#app").innerHTML = `
    <header>
      <strong>Biside</strong>
      <select id="wsPick">${workspaces
        .map((w) => `<option value="${w.id}" ${w.id === workspaceId ? "selected" : ""}>${w.title}</option>`)
        .join("")}</select>
      <div class="tabs">
        <button id="tabBoard" class="tab on">ボード</button>
        <button id="tabNote" class="tab">ノート</button>
      </div>
      <nav id="crumbs"></nav>
      <span id="who">${user.email}</span>
      <button id="newWs">＋ ワークスペース</button>
      <button id="invite">招待</button>
      <button id="out">ログアウト</button>
      <span id="state" class="down">未接続</span>
    </header>
    <main>
      <div id="board" class="board"></div>
      <div id="note" hidden>
        <div id="rows" class="outline"></div>
        <button id="add" class="add">＋ 行を足す</button>
      </div>
      <div id="inviteBox"></div>
      <!-- **ID を人に運ばせない。** 前はここに「この ID を Mac の設定に貼れ」と出していたが、
           いまは招待リンクだけで繋がる。代わりに置くのは「アプリで開く」への入口。 -->
      <div class="invite" id="macBox"></div>
    </main>`;

  const store = new OutlineStore();
  const link = new SyncLink(workspaceId, store, (state: LinkState, detail) => {
    const el = $("#state");
    el.className = state;
    el.textContent = detail;
  });

  const view = new OutlineView($("#rows"), {
    store,
    send: (ops: SyncOp[]) => link.send(store.local(ops)),
    onZoom: (nodeId) => renderCrumbs(nodeId),
    onMove: (nodeId) => link.tellPresence(nodeId),
    presence: () => link.presence,
  });
  link.onPresence = () => view.render();

  function renderCrumbs(nodeId: string | null): void {
    const trail: { id: string | null; label: string }[] = [{ id: null, label: "すべて" }];
    let cur = nodeId;
    const stack: { id: string; label: string }[] = [];
    while (cur) {
      const n = store.tree.get(cur);
      if (!n) break;
      stack.unshift({ id: n.id, label: n.text || "（無題）" });
      cur = n.parentId;
    }
    trail.push(...stack);
    const nav = $("#crumbs");
    nav.innerHTML = nodeId === null ? "" : trail.map((t) => `<a href="#" data-id="${t.id ?? ""}">${t.label}</a>`).join("<i>›</i>");
    nav.querySelectorAll<HTMLAnchorElement>("a").forEach((a) =>
      a.addEventListener("click", (e) => {
        e.preventDefault();
        view.zoomTo(a.dataset.id || null);
      }),
    );
  }

  $("#wsPick").addEventListener("change", (e) => {
    location.search = `?ws=${(e.target as HTMLSelectElement).value}`;
  });
  $("#out").addEventListener("click", async () => {
    await post("/api/auth/sign-out", {});
    location.href = "/";
  });
  $("#add").addEventListener("click", () => view.appendRow());
  $("#newWs").addEventListener("click", async () => {
    const title = prompt("新しいワークスペースの名前", "共有ワークスペース");
    if (!title) return;
    const { ok, json } = await post("/api/workspaces", { title });
    if (ok) location.href = `/?ws=${json.id}`;
  });

  $("#invite").addEventListener("click", async () => {
    const email = prompt("招待する相手のメール");
    if (!email) return;
    const { ok, json } = await post(`/api/workspaces/${workspaceId}/invites`, { email });
    $("#inviteBox").innerHTML = ok
      ? `<div class="invite">${email} に渡すリンク（7 日間有効）:<br>${json.url}</div>`
      : `<div class="invite">招待を作れませんでした</div>`;
  });

  // ---- ボード（読むための面。招かれた人が最初に見るのはここ） ----
  let openCardID: string | null = null;

  async function loadBoard(): Promise<void> {
    const { ok, json } = await api(`/api/workspaces/${workspaceId}/board`);
    if (!ok) return;
    const { columns, cards } = json as { columns: BoardColumn[]; cards: BoardCard[] };
    $("#board").innerHTML = boardHTML(columns, cards);
    $("#board").querySelectorAll<HTMLButtonElement>("[data-card]").forEach((b) =>
      b.addEventListener("click", () => openCard(b.dataset.card!)),
    );
  }

  async function openCard(nodeId: string): Promise<void> {
    openCardID = nodeId;
    const panel = $("#detail");
    panel.hidden = false;
    panel.innerHTML = `<p class="muted">読み込んでいます…</p>`;
    const { ok, json } = await api(`/api/workspaces/${workspaceId}/card/${encodeURIComponent(nodeId)}`);
    if (!ok) {
      panel.innerHTML = `<p class="muted">開けませんでした。</p>`;
      return;
    }
    panel.innerHTML = detailHTML(json as CardDetail);
    panel.querySelector<HTMLButtonElement>("[data-close]")?.addEventListener("click", () => {
      panel.hidden = true;
      openCardID = null;
    });
  }

  document.body.insertAdjacentHTML("beforeend", `<aside id="detail" class="detail" hidden></aside>`);

  const showBoard = (on: boolean) => {
    $("#board").hidden = !on;
    $("#note").hidden = on;
    $("#tabBoard").classList.toggle("on", on);
    $("#tabNote").classList.toggle("on", !on);
    if (on) void loadBoard();
  };
  $("#tabBoard").addEventListener("click", () => showBoard(true));
  $("#tabNote").addEventListener("click", () => showBoard(false));
  void loadBoard();

  // **相手の Mac で動いた結果が、黙って古いまま残らないように。** 行の同期とは別口で
  // 畳んだ面を読んでいるので、届いた変更に合わせて読み直す（開いているカードも含めて）。
  let pending: number | undefined;
  const refreshSoon = () => {
    clearTimeout(pending);
    pending = setTimeout(() => {
      if (!$("#board").hidden) void loadBoard();
      if (openCardID) void openCard(openCardID);
    }, 600) as unknown as number;
  };
  store.onChange(refreshSoon);
  window.addEventListener("focus", refreshSoon);
  setInterval(refreshSoon, 6000);

  // Mac アプリへの受け渡し。入っていれば開き、入っていなければ入れ方を出す。
  renderMacBox(workspaceId);

  // 検証用の覗き口。画面から読めない「送ったか」「木がどうなっているか」を台本が確かめられる
  // ようにしておく。ここが無いと、失敗したときに DOM から推測するしかなくなる。
  (window as unknown as { __biside: unknown }).__biside = { store, view, link };

  link.start();
}

/**
 * 「Mac アプリで開く」。**入れ方の案内はここにだけ置く**（メールからは外した）。
 * 押した先は `biside://` で、入っていなければ何も起きない——だから入れ方も同じ場所に出す。
 */
function renderMacBox(workspaceId: string): void {
  const box = $("#macBox");
  box.innerHTML = `
    <button id="openMac">Mac アプリで開く</button>
    <a href="#" id="howto" class="small">アプリを入れる</a>
    <div id="howtoBox" hidden class="small"></div>`;
  $("#openMac").addEventListener("click", () => {
    // **接続先も一緒に渡す。** 押した人の Mac は、たいてい何も設定されていない
    // （参加はここで済ませていて、アプリは入れたばかり）。これが無いと、開いた先で
    // どこへ訊けばよいか分からないまま空の画面が出る。
    location.href = `biside://workspace/${workspaceId}?server=${encodeURIComponent(location.origin)}`;
  });
  $("#howto").addEventListener("click", async (e) => {
    e.preventDefault();
    const el = $("#howtoBox");
    el.hidden = false;
    const cmd = (await api("/api/bootstrap")).json?.installCommand ?? "";
    el.innerHTML = cmd
      ? `Mac のターミナルで次の 1 行を実行すると入ります（macOS 26 以上）:<br><code>${esc(cmd)}</code>`
      : "配布の案内は、招待してくれた人にお尋ねください。";
  });
}

// ---------------------------------------------------------------------- 起動

const session = await api("/api/auth/get-session");
if (location.pathname === "/link") {
  await renderLink(session.json?.user ?? null);
} else if (inviteToken) {
  // **招待があるときは、入っていてもいなくても参加の画面。** ログイン画面に but 書きを
  // 足す形にすると、何のリンクを踏んだのか読み取れない。
  await renderJoin(inviteToken, session.json?.user ?? null);
} else if (!session.json?.user) {
  const bootstrap = (await api("/api/bootstrap")).json?.available === true;
  renderGate(bootstrap ? "signup" : "signin", bootstrap);
} else {
  const { json } = await api("/api/workspaces");
  if (!json.workspaces?.length) {
    await post("/api/workspaces", { title: "はじめのワークスペース" });
    location.reload();
  } else {
    renderApp(session.json.user, json.workspaces);
  }
}
